/* eslint-disable react-refresh/only-export-components */
// Minimal toast implementation keeps the notification surface inline.
// API surface kept intentionally narrow: toast.success / toast.error + <Toaster />.
// Consumers always import from "@/components/ui/toast", not from "sonner" directly.
import { useState, useEffect, useCallback, useRef } from "react";

type ToastKind = "success" | "error";

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

type ToastListener = (item: ToastItem) => void;

export const TOAST_AUTO_DISMISS_MS = 4_000;

// Module-level event bus — no context needed; Toaster registers once.
let _seq = 0;
const _listeners: Set<ToastListener> = new Set();

function emit(kind: ToastKind, message: string): void {
  const item: ToastItem = { id: ++_seq, kind, message };
  _listeners.forEach((fn) => fn(item));
}

/** toast.success / toast.error — call from anywhere, no import of sonner needed. */
export const toast = {
  success: (message: string) => emit("success", message),
  error: (message: string) => emit("error", message),
};

// ── ToastBanner ───────────────────────────────────────────────────────────────

interface ToastBannerProps {
  item: ToastItem;
  onDismiss: (id: number) => void;
}

function ToastBanner({ item, onDismiss }: ToastBannerProps): React.JSX.Element {
  const isSuccess = item.kind === "success";
  const borderCls = isSuccess ? "border-success" : "border-rose-500/30";
  const dotCls = isSuccess ? "bg-success" : "bg-rose-400";
  const textCls = isSuccess ? "text-success" : "text-danger-soft-faint";

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={`flex items-center gap-3 rounded-xl border ${borderCls} bg-[rgba(10,14,22,0.95)] px-4 py-3 shadow-lg backdrop-blur-sm`}
    >
      <span
        className={`h-2 w-2 shrink-0 rounded-full ${dotCls}`}
        aria-hidden="true"
      />
      <span className={`text-sm font-medium ${textCls}`}>{item.message}</span>
      <button
        type="button"
        onClick={() => onDismiss(item.id)}
        className="ml-auto shrink-0 text-foreground/40 hover:text-foreground/70 transition-colors"
        aria-label="Fechar"
      >
        ✕
      </button>
    </div>
  );
}

// ── Toaster ───────────────────────────────────────────────────────────────────

// Props are accepted (matches sonner's Toaster API) but all handled internally.
export function Toaster(
  _props: Record<string, unknown> = {},
): React.JSX.Element {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    const activeTimers = timers.current;
    const listener: ToastListener = (item) => {
      setItems((prev) => [...prev.slice(-4), item]); // keep at most 5
      const tid = setTimeout(() => dismiss(item.id), TOAST_AUTO_DISMISS_MS);
      activeTimers.set(item.id, tid);
    };
    _listeners.add(listener);
    return () => {
      _listeners.delete(listener);
      activeTimers.forEach(clearTimeout);
    };
  }, [dismiss]);

  return (
    <div
      role="region"
      aria-label="Notificações"
      className="fixed bottom-4 right-4 z-[9999] flex min-h-px flex-col gap-2 w-80 max-w-[calc(100vw-2rem)]"
    >
      {items.map((item) => (
        <ToastBanner key={item.id} item={item} onDismiss={dismiss} />
      ))}
    </div>
  );
}
